import { useMemo } from 'react';
import { useFeatureFlags } from '@funtech-int/yango-shared';

import { useInstanceLayoutEffect } from '@/scenarios/legacy-player/hooks/useInstanceLayoutEffect';

import { usePlayerContentFinished } from '../../analytics/hooks/use-player-content-finished';
import { usePlayerContentStarted } from '../../analytics/hooks/use-player-content-started';

interface UsePlayerAnalyticsParams {
    contentId: string | undefined;
    psid: string;
    manifestUrl: string | undefined;
}

export const usePlayerAnalytics = ({ contentId, psid, manifestUrl }: UsePlayerAnalyticsParams) => {
    const { flags } = useFeatureFlags();

    const isDynamicPrerollEnabled = flags.get('isDynamicPrerollEnabled', 'boolean', false);

    const eventMeta = useMemo(
        () => ({
            psid: psid ?? '',
            contentId: contentId ?? '',
            manifestUrl: manifestUrl ?? '',
        }),
        [psid, contentId, manifestUrl]
    );

    const trackedPlayerContentStarted = usePlayerContentStarted(eventMeta);
    const trackedPlayerContentFinished = usePlayerContentFinished(eventMeta);

    useInstanceLayoutEffect(
        (instance) => {
            const unsubscribeFromOnContentImpression = instance.onContentImpression(() =>
                trackedPlayerContentStarted({ vsid: instance.vsid ?? '', isDynamicPrerollEnabled })
            );
            const unsubscribeFromOnFinish = instance.onFinish(() =>
                trackedPlayerContentFinished({ vsid: instance.vsid ?? '', isDynamicPrerollEnabled })
            );

            return () => {
                unsubscribeFromOnContentImpression();
                unsubscribeFromOnFinish();
            };
        },
        [trackedPlayerContentStarted, trackedPlayerContentFinished, isDynamicPrerollEnabled]
    );
};
